import { reportsPosition } from '../GscTools/metrics';
import { REPORTING_TIMEZONE, formatPeriod, searchTypeLabel } from '../GscTools/period';
import { filterLabel } from './groups';

/**
 * The scope every export carries in its head, so a file that outlives this page still says what
 * it is: which property, which surface, which windows, every rule that shaped the requests, and
 * that the groups may overlap.
 *
 * The rules are listed one per line, as Search Console was sent them. A group's numbers without
 * its rules beside them are not a measurement of anything — two people with different prefixes
 * would produce different files under the same group name — so the two are never separated.
 */
function scopeRows({ t, scope, fetchedAt }) {
    const rows = [
        [t('tools.content_groups.export.report'), t('tools.content_groups.header')],
        [t('tools.gsc.exports.property'), scope.siteUrl],
        [t('tools.gsc.coverage.current'), formatPeriod(scope.current)],
        [t('tools.gsc.coverage.baseline'), scope.baseline ? formatPeriod(scope.baseline) : ''],
        [t('tools.gsc.controls.search_type'), searchTypeLabel(scope.searchType, t)],
        [t('tools.gsc.controls.country'), scope.country || 'all'],
        [t('tools.gsc.controls.device'), scope.device || 'all'],
        [t('tools.gsc.exports.data_state'), t(`tools.gsc.data_state.${scope.dataState}`)],
        [t('tools.gsc.exports.timezone'), REPORTING_TIMEZONE],
        [t('tools.content_groups.export.aggregation'), 'byPage'],
        [t('tools.content_groups.export.overlap'), t('tools.content_groups.export.overlap_value')],
        [t('tools.gsc.exports.fetched_at'), fetchedAt ?? ''],
    ];

    for (const rule of scope.rules) {
        rows.push([t('tools.content_groups.export.rule'), rule.group, filterLabel(rule)]);
    }

    rows.push([]);
    return rows;
}

/**
 * Block 02 as a file: one row per group, both windows side by side.
 *
 * Values are the raw API ones — CTR as a fraction, position as a mean — and a metric Search
 * Console did not return stays empty rather than becoming a 0. There is no total row, for the
 * same reason the table has none.
 */
export function buildComparisonCsv({ t, scope, rows, fetchedAt }) {
    const hasPosition = reportsPosition(scope.searchType);
    const out = scopeRows({ t, scope, fetchedAt });

    out.push([
        t('tools.content_groups.comparison.group'),
        t('tools.content_groups.export.filters'),
        t('tools.gsc.columns.current_clicks'),
        t('tools.gsc.columns.prior_clicks'),
        t('tools.gsc.columns.delta_clicks'),
        t('tools.gsc.columns.delta_percent'),
        t('tools.gsc.columns.current_impressions'),
        t('tools.gsc.columns.prior_impressions'),
        t('tools.gsc.columns.current_ctr'),
        t('tools.gsc.columns.prior_ctr'),
        ...(hasPosition
            ? [t('tools.gsc.columns.current_position'), t('tools.gsc.columns.prior_position')]
            : []),
    ]);

    for (const row of rows) {
        out.push([
            row.key,
            row.filters.join(' AND '),
            row.current?.clicks,
            row.prior?.clicks,
            row.deltaClicks,
            row.deltaPercent,
            row.current?.impressions,
            row.prior?.impressions,
            row.current?.ctr,
            row.prior?.ctr,
            ...(hasPosition ? [row.current?.position, row.prior?.position] : []),
        ]);
    }

    return out;
}

/**
 * Block 04 as a file: every page retrieved for the active group in this generation, not the page
 * of rows the table happens to be showing, with the window, the retrieval state and the other
 * groups each URL also meets.
 */
export function buildPagesCsv({ t, scope, group, period, rows, stopReason, fetchedAt }) {
    const hasPosition = reportsPosition(scope.searchType);
    const out = scopeRows({ t, scope, fetchedAt });

    // The block's own window replaces nothing above it: the file states both, because the
    // matching URLs can cover fewer days than the group totals beside them.
    out.splice(
        out.length - 1,
        0,
        [t('tools.content_groups.export.active_group'), group],
        [t('tools.content_groups.export.pages_window'), formatPeriod(period)],
        [t('tools.content_groups.export.retrieval'), t(`tools.gsc.stop.${stopReason}`)],
        [
            t('tools.content_groups.export.completeness'),
            t('tools.content_groups.pages.not_inventory'),
        ],
    );

    out.push([
        t('tools.content_groups.pages.url'),
        t('tools.gsc.metrics.clicks_short'),
        t('tools.gsc.metrics.impressions_short'),
        t('tools.gsc.metrics.ctr_short'),
        ...(hasPosition ? [t('tools.gsc.metrics.position_short')] : []),
        t('tools.content_groups.pages.also_in'),
    ]);

    for (const row of rows) {
        out.push([
            row.key,
            row.clicks,
            row.impressions,
            row.ctr,
            ...(hasPosition ? [row.position] : []),
            row.alsoIn.join(' | '),
        ]);
    }

    return out;
}
